const DAY_MS = 86400000;

export const parseEmploymentDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const text = String(value).trim();
  if (!text) return null;
  if (['present', 'current', 'ongoing', 'now', 'to date'].includes(text.toLowerCase())) {
    return null;
  }
  const normalized = /^\d{4}-\d{1,2}$/.test(text) ? `${text}-01` : text;
  const date = new Date(`${normalized}T00:00:00Z`);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const isCurrentEmploymentRow = (row = {}) => {
  if (row?.is_current === true) return true;
  if (row?.is_current === false) return false;
  const end = row?.end_date ?? row?.to_date;
  if (end === null || end === undefined || String(end).trim() === '') return true;
  return ['present', 'current', 'ongoing', 'now', 'to date'].includes(String(end).trim().toLowerCase());
};

export const formatEmploymentBoundary = (value, { fallback = 'Date not available' } = {}) => {
  if (value === null || value === undefined || String(value).trim() === '') return fallback;
  const text = String(value).trim();
  if (['present', 'current', 'ongoing', 'now', 'to date'].includes(text.toLowerCase())) return 'Present';
  const parsed = parseEmploymentDate(text);
  if (!parsed) return fallback;
  return parsed.toLocaleDateString('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' });
};

export const buildEmploymentTimeline = (
  history = [],
  {
    asOfDate = new Date(),
    coverageYears = 10,
    minGapDays = 30,
  } = {},
) => {
  const asOf = asOfDate instanceof Date ? new Date(asOfDate) : new Date(asOfDate);
  const safeAsOf = Number.isNaN(asOf.getTime()) ? new Date() : asOf;
  safeAsOf.setUTCHours(0, 0, 0, 0);

  const coverageStart = new Date(safeAsOf);
  coverageStart.setUTCFullYear(coverageStart.getUTCFullYear() - coverageYears);

  const validRows = [];
  const invalidRows = [];
  (history || []).forEach((row, index) => {
    if (!row || typeof row !== 'object') {
      invalidRows.push({ index, reason: 'Employment row is not an object' });
      return;
    }
    const startValue = row.start_date ?? row.from_date;
    const endValue = row.end_date ?? row.to_date;
    const start = parseEmploymentDate(startValue);
    if (!start) {
      invalidRows.push({ index, reason: 'Missing or invalid start date', row });
      return;
    }
    const currentTokens = new Set(['present', 'current', 'ongoing', 'now', 'to date']);
    const endText = String(endValue ?? '').trim().toLowerCase();
    const current = isCurrentEmploymentRow(row);
    if (row?.is_current === false && (!endText || currentTokens.has(endText))) {
      invalidRows.push({ index, reason: 'End date is required when employment is not current', row });
      return;
    }
    if (row?.is_current === true && endText && !currentTokens.has(endText)) {
      invalidRows.push({ index, reason: 'A role marked current cannot also have an end date', row });
      return;
    }
    const parsedEnd = current ? safeAsOf : parseEmploymentDate(endValue);
    if (!current && !parsedEnd) {
      invalidRows.push({ index, reason: 'Missing or invalid end date', row });
      return;
    }
    if (parsedEnd < start) {
      invalidRows.push({ index, reason: 'Employment end date is before start date', row });
      return;
    }
    validRows.push({
      row,
      start,
      end: parsedEnd > safeAsOf ? safeAsOf : parsedEnd,
      current,
    });
  });

  validRows.sort((a, b) => a.start - b.start || a.end - b.end);

  const merged = [];
  for (const interval of validRows) {
    if (!merged.length || interval.start > merged[merged.length - 1].end) {
      merged.push({
        start: interval.start,
        end: interval.end,
        startRow: interval.row,
        endRow: interval.row,
        containsCurrent: interval.current,
      });
      continue;
    }
    const active = merged[merged.length - 1];
    active.containsCurrent = active.containsCurrent || interval.current;
    if (interval.end > active.end) {
      active.end = interval.end;
      active.endRow = interval.row;
    }
  }

  const gaps = [];
  let gapNum = 0;

  if (merged.length) {
    const earliest = merged[0];
    const preGapDays = Math.floor((earliest.start - coverageStart) / DAY_MS);
    if (preGapDays >= minGapDays) {
      gapNum += 1;
      gaps.push({
        gap_id: `gap_${gapNum}`,
        gap_type: 'pre_history',
        gap_start: coverageStart.toISOString().slice(0, 10),
        gap_end: earliest.row?.start_date || earliest.row?.from_date || earliest.start.toISOString().slice(0, 10),
        duration_days: preGapDays,
        duration_months: Math.round((preGapDays / 30) * 10) / 10,
        label: 'Before first employment entry',
      });
    }

    for (let index = 0; index < merged.length - 1; index += 1) {
      const previous = merged[index];
      const following = merged[index + 1];
      const gapDays = Math.floor((following.start - previous.end) / DAY_MS);
      if (gapDays < minGapDays) continue;
      gapNum += 1;
      gaps.push({
        gap_id: `gap_${gapNum}`,
        gap_type: 'inter_entry',
        gap_start: previous.endRow?.end_date || previous.endRow?.to_date || previous.end.toISOString().slice(0, 10),
        gap_end: following.startRow?.start_date || following.startRow?.from_date || following.start.toISOString().slice(0, 10),
        duration_days: gapDays,
        duration_months: Math.round((gapDays / 30) * 10) / 10,
        label: `Between ${previous.endRow?.employer_name || previous.endRow?.company || 'employment'} and ${following.startRow?.employer_name || following.startRow?.company || 'employment'}`,
      });
    }

    const last = merged[merged.length - 1];
    const trailingDays = Math.floor((safeAsOf - last.end) / DAY_MS);
    if (trailingDays >= minGapDays) {
      gapNum += 1;
      gaps.push({
        gap_id: `gap_${gapNum}`,
        gap_type: 'trailing',
        gap_start: last.endRow?.end_date || last.endRow?.to_date || last.end.toISOString().slice(0, 10),
        gap_end: 'present',
        duration_days: trailingDays,
        duration_months: Math.round((trailingDays / 30) * 10) / 10,
        label: `After ${last.endRow?.employer_name || last.endRow?.company || 'last employment'} to present`,
      });
    }
  }

  const coverageIntervals = merged
    .map((interval) => [
      interval.start < coverageStart ? coverageStart : interval.start,
      interval.end > safeAsOf ? safeAsOf : interval.end,
    ])
    .filter(([start, end]) => start < end);

  const totalRequired = Math.max(0, Math.floor((safeAsOf - coverageStart) / DAY_MS));
  const totalCovered = coverageIntervals.reduce(
    (sum, [start, end]) => sum + Math.floor((end - start) / DAY_MS),
    0,
  );

  return {
    detectedGaps: gaps,
    invalidRows,
    coverageSummary: {
      coverage_start: coverageStart.toISOString().slice(0, 10),
      coverage_end: safeAsOf.toISOString().slice(0, 10),
      total_days_required: totalRequired,
      total_days_covered: totalCovered,
      coverage_percent: totalRequired > 0 ? Math.round((totalCovered / totalRequired) * 100) : 0,
      has_entries: validRows.length > 0,
      has_current_employment: validRows.some((row) => row.current),
      invalid_entry_count: invalidRows.length,
    },
  };
};
